// src/pages/Logs.jsx - FULLY INTEGRATED
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Download,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  MoreVertical,
  ExternalLink,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.jsx';
import { apiCall } from '../utils/api';

export default function Logs() {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const initialConnectorId = searchParams.get('connector') || '';
  const [filters, setFilters] = useState({
    connector: initialConnectorId,  // now stores connector_config_id (numeric string)
    status: '',
    search: '',
  });

  // Load logs on mount
  useEffect(() => {
    loadLogs();
  }, []);

  async function loadLogs() {
    setLoading(true);
    setError(null);

    try {
      const res = await apiCall('/logs/sync-runs?limit=500');
      if (res.ok) {
        const data = await res.json();
        const transformedLogs = (data.runs || []).map(transformRun);
        setLogs(transformedLogs);
      } else {
        throw new Error('Failed to load logs');
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
      setError(err.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadLogs();
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleExport = () => {
    // Export filtered logs as JSON
    const filtered = getFilteredLogs();
    const dataStr = JSON.stringify(filtered, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `connex-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  function getFilteredLogs() {
    return logs.filter(log => {
      // filters.connector is either a numeric connector_config_id (from URL or
      // the dropdown) or empty for 'all'. Compare as strings since URL params
      // are strings.
      const matchesConnector = !filters.connector
        || String(log.connectorConfigId) === String(filters.connector);
      const matchesStatus = !filters.status || log.status === filters.status;
      const matchesSearch = !filters.search || 
        log.event.toLowerCase().includes(filters.search.toLowerCase()) ||
        log.connector.toLowerCase().includes(filters.search.toLowerCase());
      
      return matchesConnector && matchesStatus && matchesSearch;
    });
  }

  // Get unique {id, name} pairs for the connector dropdown.
  // Map by id to dedupe; the most recent name wins.
  const connectorMap = new Map();
  logs.forEach(log => {
    if (log.connectorConfigId != null) {
      connectorMap.set(String(log.connectorConfigId), log.connector || `Connector #${log.connectorConfigId}`);
    }
  });
  const uniqueConnectors = Array.from(connectorMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredLogs = getFilteredLogs();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Activity Logs
          </h1>
        </div>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
        <Button onClick={loadLogs}>
          <RefreshCw className="w-4 h-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Activity Logs
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Monitor sync operations and connector activity across all data sources
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="secondary" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            <Download className="w-4 h-4" />
            Export Logs
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <FiltersRow
        filters={filters}
        onFilterChange={handleFilterChange}
        connectors={uniqueConnectors}
      />

      {/* Results Summary */}
      {(filters.connector || filters.status || filters.search) && (
        <div className="flex items-center gap-2">
          <Badge variant="neutral">
            {filteredLogs.length} result{filteredLogs.length !== 1 ? 's' : ''}
          </Badge>
          <button
            onClick={() => setFilters({ connector: '', status: '', search: '' })}
            className="text-xs text-[#6a6a6a] hover:text-white transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Logs Table */}
      <LogsTable logs={filteredLogs} />
    </div>
  );
}

// ==========================================
// FILTERS ROW
// ==========================================
function FiltersRow({ filters, onFilterChange, connectors }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Connector Filter */}
      <select
        value={filters.connector}
        onChange={(e) => onFilterChange('connector', e.target.value)}
        className="h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
      >
        <option value="">All Connectors</option>
        {connectors.map(c => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      {/* Status Filter */}
      <select
        value={filters.status}
        onChange={(e) => onFilterChange('status', e.target.value)}
        className="h-9 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
      >
        <option value="">All Statuses</option>
        <option value="success">Success</option>
        <option value="warning">Warning</option>
        <option value="error">Error</option>
      </select>

      {/* Search Input */}
      <div className="relative flex-1 min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
        <Input
          type="text"
          placeholder="Search events..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="pl-10"
        />
      </div>
    </div>
  );
}

// ==========================================
// LOGS TABLE
// ==========================================
function LogsTable({ logs }) {
  if (logs.length === 0) {
    return (
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
        <div className="flex flex-col items-center justify-center py-12">
          <Clock className="w-12 h-12 text-[#6a6a6a] mb-4" />
          <p className="text-sm font-medium text-white mb-1">
            No activity logs found
          </p>
          <p className="text-sm text-[#6a6a6a]">
            Try adjusting your filters or check back later
          </p>
        </div>
      </div>
    );
  }

  return (
    <TableWrapper>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Connector</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Timestamp</TableHead>
            <TableHead className="text-right">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <LogRow key={log.id} log={log} />
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
}

// ==========================================
// LOG ROW
// ==========================================
function LogRow({ log }) {
  return (
    <TableRow>
      {/* Connector */}
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-blue-500">
              {log.connector ? log.connector.substring(0, 2).toUpperCase() : 'UN'}
            </span>
          </div>
          <span className="font-medium">{log.connector || 'Unknown'} • ID: {log.connectorConfigId || 'N/A'}</span>
        </div>
      </TableCell>

      {/* Event */}
      <TableCell>
        <div className="flex items-center gap-2">
          <StatusIcon status={log.status} />
          <span className="text-[#a0a0a0]">{log.event}</span>
        </div>
      </TableCell>

      {/* Status */}
      <TableCell>
        <StatusBadge status={log.status} />
      </TableCell>

      {/* Timestamp */}
      <TableCell className="text-[#a0a0a0]">
        {log.timestamp}
      </TableCell>

      {/* Details */}
      <TableCell className="text-right">
        <LogDetailsDropdown log={log} />
      </TableCell>
    </TableRow>
  );
}

// ==========================================
// STATUS ICON
// ==========================================
function StatusIcon({ status }) {
  const config = {
    success: { icon: CheckCircle, className: 'text-green-500' },
    warning: { icon: AlertCircle, className: 'text-amber-500' },
    error: { icon: XCircle, className: 'text-red-500' },
  };

  const { icon: Icon, className } = config[status] || config.warning;
  return <Icon className={`w-4 h-4 flex-shrink-0 ${className}`} />;
}

// ==========================================
// STATUS BADGE
// ==========================================
function StatusBadge({ status }) {
  const variants = {
    success: 'success',
    warning: 'warning',
    error: 'error',
  };

  const labels = {
    success: 'Success',
    warning: 'Warning',
    error: 'Failed',
  };

  return (
    <Badge variant={variants[status] || 'neutral'}>
      {labels[status] || 'Unknown'}
    </Badge>
  );
}

// ==========================================
// LOG DETAILS DROPDOWN
// ==========================================
function LogDetailsDropdown({ log }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center justify-center w-8 h-8 text-[#a0a0a0] hover:text-white hover:bg-[#222222] rounded-lg transition-colors">
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 border-b border-[#2a2a2a]">
          <p className="text-xs font-medium text-[#a0a0a0] uppercase tracking-wide">
            Details
          </p>
        </div>
        
        <div className="px-2 py-2 space-y-1">
          <DetailRow label="Duration" value={log.duration} />
          <DetailRow label="Records" value={log.records} />
          {log.entity && <DetailRow label="Entity" value={log.entity} />}
          {log.runType && <DetailRow label="Type" value={log.runType} />}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ==========================================
// DETAIL ROW
// ==========================================
function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-[#a0a0a0]">{label}</span>
      <span className="text-xs font-medium text-white">{value}</span>
    </div>
  );
}

// ==========================================
// DATA TRANSFORMATION FUNCTIONS
// ==========================================

function transformRun(run) {
  return {
    id: run.id,
    connectorConfigId: run.connector_config_id,
    connector: run.connector_display_name || 'Unknown Connector',
    event: buildEventText(run),
    status: mapStatus(run.status),
    timestamp: formatTimestamp(run.started_at),
    duration: formatDuration(run.duration_seconds, run.status),
    records: formatRecords(run),
    entity: run.entity,
    runType: run.run_type,
    rawData: run, // Keep raw data for reference
  };
}

function buildEventText(run) {
  const entity = run.entity || 'data';
  const runType = run.run_type || 'sync';
  
  if (run.status === 'success') {
    return `${capitalize(runType)} sync completed · ${entity}`;
  } else if (run.status === 'error') {
    return `Sync failed · ${entity}`;
  } else if (run.status === 'running') {
    return `Sync in progress · ${entity}`;
  }
  
  return `Sync ${run.status} · ${entity}`;
}

function mapStatus(backendStatus) {
  // Backend: "running" | "success" | "error"
  // Frontend: "success" | "warning" | "error"
  if (backendStatus === 'running') return 'warning';
  if (backendStatus === 'success') return 'success';
  if (backendStatus === 'error') return 'error';
  return 'warning';
}

function formatTimestamp(isoString) {
  if (!isoString) return 'Unknown';
  
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}

function formatDuration(seconds, status) {
  // If still running or no duration
  if (status === 'running' || !seconds) {
    return status === 'running' ? 'In progress' : 'N/A';
  }
  
  if (seconds < 1) return '< 1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  
  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
}

function formatRecords(run) {
  // Backend might have rows_inserted and rows_updated
  // For now, return N/A since these fields aren't in the response
  // If they're added later, use: run.rows_inserted + run.rows_updated
  return 'N/A';
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}